import { useCallback, useState } from "react";
import { useGlobalContext } from "../context/useGlobalContext";
import { getRestoHosts, getHostRestoById, getHostRestoDetailsById, getCeoResto, getCeoRestoHosts } from "../services/restoHostService"
import { getRestoHostsByOwner } from "../services/tenantOwnerService";
import { useAvatar } from "./useAvatar";
import { useUser } from "./useUser";
import { useResto } from "./useResto";

export const useRestoUsers = () => {
    const { hostResto, setHostResto, hostByResto, setHostBResto, ceoRestoHost, setCeoRestoHost } = useGlobalContext()
    const { getAvatar } = useAvatar()
    const { isCEO, isOwner } = useUser();
    const { resto } = useResto();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const normalizeList = useCallback((payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.hosts)) return payload.hosts;
        if (Array.isArray(payload?.users)) return payload.users;
        return [];
    }, []);

    const getList = useCallback(async (restoIdOverride) => {
        if (isOwner) {
            const restoId = restoIdOverride || resto?._id || resto?.id || resto?.place_id || resto?.restoId;
            if (!restoId) return [];
            return normalizeList(await getRestoHostsByOwner(restoId));
        } else if (isCEO) {
            return normalizeList(await getCeoResto());
        } else {
            const restoId = restoIdOverride || resto?._id || resto?.id || resto?.place_id || resto?.restoId;
            return normalizeList(await getRestoHosts(restoId));
        }
    }, [isOwner, isCEO, resto, normalizeList]);

    const getAllRestoHost = useCallback(async (restoIdOverride) => {
        try {
            const res = await getList(restoIdOverride);
            const result = await Promise.all((res || [])?.map(async e => {
                e["cover"] = await getAvatar(e?.cover, e?.name, false);
                return e;
            }))

            setHostResto(result);
            return res;
        } catch (error) {
            console.error("Error en getAllRestoHost:", error);
            setHostResto([]);
            return [];
        }
    }, [getList, getAvatar, setHostResto])

    const getHostById = useCallback(async (id) => {
        if (!id) {
            setError("ID de host es requerido");
            return null;
        }
        try {
            setLoading(true);
            setError(null);
            const listMatch =
                hostResto?.find((item) => String(item?._id || item?.id) === String(id)) || null;
            let res = listMatch;

            if (!res && isOwner) {
                res = null;
            } else if (!res) {
                try {
                    res = await getHostRestoDetailsById(id);
                } catch (detailsError) {
                    console.warn("Fallback getHostRestoDetailsById failed, trying basic host endpoint:", detailsError);
                    res = await getHostRestoById(id);
                }
            }
            console.log("[useRestoUsers] getHostById result", {
                isOwner,
                requestedId: id,
                found: Boolean(res),
                hostKeys: res ? Object.keys(res) : [],
                hostPreview: res ? {
                    _id: res?._id,
                    id: res?.id,
                    name: res?.name,
                    points: res?.points,
                    hasTransactions: Array.isArray(res?.transactions),
                    transactionsLength: Array.isArray(res?.transactions) ? res.transactions.length : null,
                    hasEvaluations: Array.isArray(res?.evaluations),
                    evaluationsLength: Array.isArray(res?.evaluations) ? res.evaluations.length : null,
                } : null,
            });
            setHostBResto(res);
            return res;
        } catch (error) {
            console.error("Error en getHostById:", error);
            setError(error.message || "Error al cargar datos del host");
            setHostBResto(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [isOwner, hostResto, setHostBResto]);

    const getHostCeoResto = useCallback(async () => {
        try {
            const res = await getCeoRestoHosts();
            setCeoRestoHost(res);
            return res;
        } catch (error) {
            console.error("Error en getHostCeoResto:", error);
            setCeoRestoHost([]);
            return [];
        }
    }, [setCeoRestoHost]);

    return {
        hostResto,
        hostByResto,
        ceoRestoHost,
        loading,
        error,
        getAllRestoHost,
        getHostById,
        getHostCeoResto
    }
}
